/**
 * @group Timing
 * @summary Timing constants for delays, timeouts, and durations
 * 
 * @remarks
 * This constant contains timing values used throughout the application
 * for consistent delays, timeouts, and duration settings.
 * 
 * @example
 * ```typescript
 * import { TIMING_CONSTANTS } from '@shared/constants';
 * 
 * setTimeout(() => {
 *   // Do something
 * }, TIMING_CONSTANTS.DEFAULT_DELAY);
 * 
 * const staleTime = TIMING_CONSTANTS.CACHE_STALE_TIME;
 * ```
 */
export const TIMING_CONSTANTS = {
  // Default delays
  DEFAULT_DELAY: 1000, // 1 second
  SHORT_DELAY: 500, // 500ms
  LONG_DELAY: 2000, // 2 seconds
  
  // UI timing
  COPY_FEEDBACK_DURATION: 1500, // 1.5 seconds
  LOADING_RESET_DELAY: 1000, // 1 second
  
  // Cache timing
  CACHE_STALE_TIME: 5 * 60 * 1000, // 5 minutes
  CACHE_GC_TIME: 10 * 60 * 1000, // 10 minutes
  
  // Network timing
  REQUEST_TIMEOUT: 30000, // 30 seconds
  RETRY_DELAY: 1000, // 1 second
  WEBHOOK_DELIVERY_TIMEOUT_MS: 5000, // 5 seconds for webhook POST
  WEBHOOK_RETRY_MAX_ATTEMPTS: 3,
  WEBHOOK_RETRY_INITIAL_DELAY_MS: 1000,
  WEBHOOK_RETRY_MAX_DELAY_MS: 5000,
  WEBHOOK_RETRY_FACTOR: 2,
  
  // Animation timing
  ANIMATION_DURATION: 300, // 300ms
  TRANSITION_DURATION: 200, // 200ms
} as const;
